import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, DonkeyMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import JourneySelect from "@/pages/JourneySelect";
import AnswerRecorder from "@/components/AnswerRecorder";
import {
  Send, HelpCircle, X, Mic, ArrowRight, Save, Loader2,
} from "lucide-react";

function ProgressBar({ value }) {
  return (
    <div className="h-1 w-full bg-white/8 rounded-full overflow-hidden" data-testid="interview-progress">
      <div className="h-full bg-donkey-orange transition-all duration-500" style={{ width: `${Math.round(value * 100)}%` }} />
    </div>
  );
}

function AnswerField({ q, value, setValue }) {
  const type = q.input_type;
  if (type === "textarea") {
    return (
      <Textarea
        value={value || ""} onChange={(e) => setValue(e.target.value)}
        placeholder={q.placeholder || "Type your answer…"}
        className="min-h-[120px] bg-donkey-deep border-white/15 text-donkey-warm resize-none"
        data-testid="answer-textarea"
      />
    );
  }
  if (type === "number" || type === "currency") {
    return (
      <div className="flex items-center gap-2">
        {type === "currency" && <span className="text-donkey-orange font-mono text-lg">{q.unit || "£"}</span>}
        <Input type="number" value={value || ""} onChange={(e) => setValue(e.target.value)}
          placeholder={q.placeholder || "0"} min={q.min} max={q.max}
          className="bg-donkey-deep border-white/15 text-donkey-warm font-mono" data-testid="answer-number" />
        {type === "number" && q.unit && <span className="text-donkey-grey text-sm">{q.unit}</span>}
      </div>
    );
  }
  if (type === "slider") {
    const v = value ?? q.min ?? 0;
    return (
      <div className="pt-2" data-testid="answer-slider">
        <Slider value={[Number(v)]} min={q.min ?? 0} max={q.max ?? 100} step={q.step ?? 1}
          onValueChange={(val) => setValue(val[0])} />
        <div className="mt-3 text-center font-mono text-donkey-orange">{v}{q.unit ? ` ${q.unit}` : ""}</div>
      </div>
    );
  }
  if (type === "single_select") {
    return (
      <div className="grid gap-2.5" data-testid="answer-single">
        {(q.options || []).map((opt) => (
          <button key={opt} type="button" onClick={() => setValue(opt)}
            className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
              value === opt ? "border-donkey-orange bg-donkey-orange/10 text-donkey-warm"
                : "border-white/12 bg-donkey-deep text-donkey-warm/80 hover:border-white/30"}`}
            data-testid={`option-${opt.slice(0, 20)}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (type === "multi_select") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => setValue(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    return (
      <div className="grid gap-2.5" data-testid="answer-multi">
        {(q.options || []).map((opt) => (
          <button key={opt} type="button" onClick={() => toggle(opt)}
            className={`flex items-center gap-3 text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
              arr.includes(opt) ? "border-donkey-orange bg-donkey-orange/10 text-donkey-warm"
                : "border-white/12 bg-donkey-deep text-donkey-warm/80 hover:border-white/30"}`}>
            <Checkbox checked={arr.includes(opt)} className="border-white/30 pointer-events-none" />
            {opt}
          </button>
        ))}
      </div>
    );
  }
  return (
    <Input value={value || ""} onChange={(e) => setValue(e.target.value)}
      placeholder={q.placeholder || "Type your answer…"}
      className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="answer-text" />
  );
}

export default function Interview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interviewId, setInterviewId] = useState(id || null);
  const [question, setQuestion] = useState(null);
  const [history, setHistory] = useState([]);
  const [value, setValue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [starting, setStarting] = useState(!id);
  const [journey, setJourney] = useState(null);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);

  const scrollDown = () => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);
  };

  const startNew = useCallback(async (chosenJourney) => {
    setStarting(true);
    try {
      const { data } = await api.post("/interviews", { journey: chosenJourney });
      setInterviewId(data.interview_id);
      setQuestion(data.question);
      if (!user) localStorage.setItem("guest_interview_id", data.interview_id);
      navigate(`/interview/${data.interview_id}`, { replace: true });
    } catch (e) {
      const code = e.response?.status;
      if (code === 401) {
        toast.error("Please sign in, then buy a verdict to begin.");
        navigate("/login?next=/pricing");
      } else if (code === 402) {
        toast.error(formatApiError(e.response?.data?.detail) || "You need a verdict to begin.");
        navigate("/pricing");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not start.");
      }
    } finally {
      setStarting(false);
    }
  }, [navigate, user]);

  const loadExisting = useCallback(async (iid) => {
    try {
      const { data } = await api.get(`/interviews/${iid}`);
      setHistory(data.history || []);
      if (data.status !== "in_progress") {
        // already done — go review
        navigate(`/review/${iid}`, { replace: true });
        return;
      }
      setQuestion(data.current_question);
    } catch (e) {
      toast.error("Could not load interview. Starting a new one.");
      navigate("/interview", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (id) { setInterviewId(id); loadExisting(id); }
    else { setStarting(false); }
    // eslint-disable-next-line
  }, [id]);

  const canSubmit = () => {
    if (!question) return false;
    if (question.input_type === "multi_select") return Array.isArray(value) && value.length > 0;
    if (question.input_type === "slider") return true;
    return value !== null && value !== undefined && String(value).trim() !== "";
  };

  const submit = async () => {
    if (!canSubmit() || busy) return;
    setBusy(true);
    const answered = Array.isArray(value) ? value.join(", ") : String(value);
    const entry = { field: question.field, category: question.category, question: question.message, answer: value };
    setHistory((h) => [...h, { ...entry, answerText: answered }]);
    setValue(null);
    scrollDown();
    try {
      const { data } = await api.post(`/interviews/${interviewId}/answer`, entry);
      setQuestion(data.question);
      setShowWhy(false);
      scrollDown();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The donkey stumbled. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post(`/interviews/${interviewId}/summary`);
      navigate(`/review/${interviewId}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not finish.");
      setBusy(false);
    }
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported in this browser."); return; }
    const rec = new SR();
    rec.lang = "en-GB"; rec.interimResults = false;
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setValue((v) => (v ? v + " " : "") + text);
    };
    rec.onerror = () => toast.error("Couldn't hear you clearly.");
    rec.start();
    recognitionRef.current = rec;
  };

  const voiceCapable = question && ["text", "textarea"].includes(question.input_type);
  const progress = question?.progress ?? Math.min(history.length / 14, 0.9);
  const complete = question?.complete;

  // No interview id yet and no journey chosen → show journey selection
  if (!id && !journey) {
    return <JourneySelect onSelect={(j) => { setJourney(j); startNew(j); }} />;
  }

  if (starting || !question) {
    return (
      <div className="min-h-screen bg-donkey-bg grain flex flex-col items-center justify-center gap-5">
        <DonkeyMark size={56} className="animate-pulse-soft" />
        <p className="text-donkey-grey">Waking the donkey…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-donkey-bg grain flex flex-col">
      {/* Top bar */}
      <div className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
          <Logo showText={false} />
          <div className="flex-1 max-w-md"><ProgressBar value={progress} /></div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-donkey-grey hover:text-donkey-warm"
              onClick={() => { toast.success("Progress saved. Come back any time."); navigate(user ? "/dashboard" : "/"); }}
              data-testid="interview-save-exit">
              <Save className="w-4 h-4 mr-1.5" /> Save & exit
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 mx-auto max-w-5xl w-full px-5 sm:px-8 py-8 grid lg:grid-cols-3 gap-8">
        {/* Conversation */}
        <div className="lg:col-span-2 flex flex-col">
          <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto max-h-[calc(100vh-16rem)] pr-1">
            {history.map((h, i) => (
              <div key={i} className="space-y-3 animate-fade-up">
                <div className="flex gap-3">
                  <DonkeyMark size={30} className="shrink-0 mt-1" />
                  <div className="card-donkey rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-donkey-warm/90 max-w-[85%]">
                    {h.question}
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-donkey-orange/15 border border-donkey-orange/25 rounded-2xl rounded-tr-sm px-4 py-3 text-sm text-donkey-warm max-w-[85%]">
                    {h.answerText || (Array.isArray(h.answer) ? h.answer.join(", ") : String(h.answer))}
                  </div>
                </div>
              </div>
            ))}
            {/* Current question */}
            {!complete && (
              <div className="flex gap-3 animate-fade-up">
                <DonkeyMark size={30} className="shrink-0 mt-1" />
                <div className="card-donkey rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-donkey-warm max-w-[85%]">
                  {question.message}
                  {question.why && (
                    <button onClick={() => setShowWhy((s) => !s)}
                      className="mt-2 flex items-center gap-1.5 text-xs text-donkey-orange hover:underline"
                      data-testid="why-asking-btn">
                      <HelpCircle className="w-3.5 h-3.5" /> Why are you asking this?
                    </button>
                  )}
                  {showWhy && question.why && (
                    <p className="mt-2 text-xs text-donkey-grey border-t border-white/10 pt-2">{question.why}</p>
                  )}
                </div>
              </div>
            )}
            {complete && (
              <div className="flex gap-3 animate-fade-up">
                <DonkeyMark size={30} className="shrink-0 mt-1" />
                <div className="card-donkey rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-donkey-warm max-w-[85%]">
                  {question.message || "That's plenty to work with. Let's review what I heard."}
                </div>
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="mt-6 border-t border-white/10 pt-5">
            {complete ? (
              <Button onClick={finish} disabled={busy}
                className="w-full bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold h-12"
                data-testid="review-what-i-said-btn">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</> : <>Review what I said <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            ) : (
              <>
                <AnswerField q={question} value={value} setValue={setValue} />
                {["text", "textarea"].includes(question.input_type) && (
                  <AnswerRecorder onTranscript={(t) => setValue((v) => (v && v.trim() ? v.trim() + " " : "") + t)} />
                )}
                <div className="mt-3 flex items-center justify-end gap-3">
                  <Button onClick={submit} disabled={!canSubmit() || busy}
                    className="bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold px-6"
                    data-testid="submit-answer-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <Send className="w-4 h-4 ml-2" /></>}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Donkey guide panel */}
        <aside className="hidden lg:block">
          <div className="card-donkey rounded-2xl p-6 sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <DonkeyMark size={28} />
              <span className="font-heading font-semibold text-donkey-warm">The Donkey</span>
            </div>
            <p className="text-sm text-donkey-grey leading-relaxed">
              Let's begin with what you actually know — not what an internet guru says you should sell.
            </p>
            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-xs uppercase tracking-[0.2em] text-donkey-grey mb-2">Now covering</p>
              <p className="text-sm text-donkey-orange capitalize">
                {(question.category || "").replace(/_/g, " ")}
              </p>
            </div>
            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-xs text-donkey-grey">Answered so far</p>
              <p className="font-mono text-2xl text-donkey-warm">{history.length}</p>
            </div>
            {!user && (
              <p className="mt-5 pt-5 border-t border-white/10 text-xs text-donkey-grey">
                You're a guest. You can finish the interview now — you'll create an account to save it and see your verdict.
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
